import { useState, useEffect, useCallback, useRef } from 'react';
import { impactService } from '../../../../services/impactService';
import usePolling from '../../../../hooks/usePolling';
import { POLLING_INTERVALS } from '../../../../config/polling';
import { logError, logWarning } from '../../../../utils/errorLogging';
import { isResolvedImpact } from '../../../../utils/impactStatus';

/**
 * Builds a PUT payload by spreading the full impact object and applying
 * status overrides. The backend requires a full replacement body (PUT),
 * so all existing fields must be preserved.
 */
const buildUpdatePayload = (impact, overrides) => ({
  ...impact,
  ...overrides
});

/**
 * useReportedImpacts
 *
 * Fetches and polls reported impacts for a given problem.
 * Results are ordered Open before Resolved. Within the Open group, impacts sort by
 * createdAt descending; within the Resolved group, by lastModifiedAt descending
 * (falling back to createdAt). Accepted caveat: editing an already-resolved impact
 * bumps it up the Resolved group because lastModifiedAt also moves on edit.
 *
 * @param {string}  problemNumber - Problem number (e.g. "PRB0001234")
 * @param {boolean} enabled       - Whether to fetch and poll. Callers MUST pass false for a
 *                                  temporary problem: the backend refuses impacts on one
 *                                  (403) by design, and nothing here stops an ALREADY-ARMED
 *                                  interval once a refusal comes back — see the asymmetry
 *                                  note above usePolling. Derive it from the record's
 *                                  `temporary` flag, never from a `PRBT` number prefix —
 *                                  same note.
 * @param {string}  userEmail     - Current user's email (passed through for ownership checks in consuming components)
 * @returns {{ impacts, loading, refreshing, lastUpdated, error, refresh, markResolved, markActive }}
 */
export const useReportedImpacts = (problemNumber, enabled = false, userEmail = '') => {
  const [impacts, setImpacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const impactsRef = useRef(impacts);

  // Controller for the in-flight BACKGROUND POLL. Declared up here, above the
  // mount/identity effect, because that effect's cleanup aborts it when
  // problemNumber changes -- see the cleanup comment below.
  const pollAbortRef = useRef(null);

  // Holds the problemNumber whose unattributable 403 has already been logged, so
  // the warning in the PrivilegeError branch fires once per ENTRY INTO the refused
  // state instead of once per 30-second poll. A ref, not state: it must not re-render.
  // Read the branch itself for why the "once" is load-bearing.
  //
  // CLEARED ON A SUCCESSFUL FETCH (see the success path above) — that is what makes the
  // key a transition rather than a mount. Before the clear existed the ref was set once
  // and never reset, which inverted the observability in the case that matters most: a
  // 403 arriving mid-session on a background poll logged one line and then repeated the
  // refused request every POLLING_INTERVALS.INCIDENT_IMPACTS with nothing further in the
  // logs for the life of the mount.
  //
  // KNOWN LIMITATION, stated rather than implied: the ref is per-mount, so in the case
  // that actually drives 403 volume — ExpandedContent mounting and unmounting this
  // consumer on every card expand/collapse — each mount gets a fresh ref and the rate
  // limit suppresses nothing. Hoisting the dedupe to module scope would fix that by
  // trading a per-mount ref for unbounded global state with cross-test pollution, which
  // is a worse deal; the upstream `enabled: false` gate is what actually removes those
  // requests.
  const forbiddenLoggedForRef = useRef(null);

  useEffect(() => {
    impactsRef.current = impacts;
  }, [impacts]);

  /**
   * Sort impacts Open group before Resolved group. The Open group sorts by createdAt
   * descending; the Resolved group sorts by lastModifiedAt descending (fallback createdAt).
   * Accepted caveat: editing an already-resolved impact bumps it up the Resolved group
   * because lastModifiedAt also moves on edit.
   */
  const sortImpacts = useCallback((items) => {
    if (!Array.isArray(items)) return [];

    return [...items].sort((a, b) => {
      const aRank = isResolvedImpact(a) ? 1 : 0;
      const bRank = isResolvedImpact(b) ? 1 : 0;
      if (aRank !== bRank) return aRank - bRank;

      if (aRank === 1) {
        // Resolved group: most recently resolved first, approximated by
        // lastModifiedAt (fall back to createdAt when absent).
        const aTime = new Date(a.lastModifiedAt || a.createdAt).getTime() || 0;
        const bTime = new Date(b.lastModifiedAt || b.createdAt).getTime() || 0;
        return bTime - aTime;
      }

      // Open group: newest reported first.
      const aTime = new Date(a.createdAt).getTime() || 0;
      const bTime = new Date(b.createdAt).getTime() || 0;
      return bTime - aTime;
    });
  }, []);

  const fetchImpacts = useCallback(async (isBackgroundRefresh = false, signal = null) => {
    const hasCurrentData = impactsRef.current.length > 0;

    try {
      if (isBackgroundRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const fetchOptions = signal ? { signal } : {};
      const result = await impactService.getImpacts(problemNumber, {}, fetchOptions);
      const sorted = sortImpacts(result.impacts || []);

      setImpacts(sorted);
      setLastUpdated(new Date());
      setError(null);
      setIsInitialLoad(false);
      // A successful read re-arms the 403 warning, so the log keys on TRANSITIONS INTO
      // the refused state rather than on the first refusal per identity. Without this
      // the ref, once set, is never cleared for the life of the mount: a 403 that starts
      // mid-poll (WAF block, permission change) logs once and then 403s every
      // POLLING_INTERVALS.INCIDENT_IMPACTS in silence. See the ref's declaration.
      forbiddenLoggedForRef.current = null;
    } catch (err) {
      // Ignore aborted requests (user navigated away or problem changed)
      if (err.name === 'AbortError') {
        return;
      }

      // 403 means impacts aren't available for this problem. Treat as empty, not an
      // error — don't show a failure message.
      //
      // THIS IS NOW A RESIDUAL PATH, NOT THE PRIMARY ONE. The expected cause —
      // a temporary problem, which the backend refuses by design
      // (handler_impact.go:372) — is prevented upstream as of US1950081: consumers
      // pass `enabled: false` for a temporary record, so no request is made to be
      // refused. What can still land here is a 403 the hook cannot attribute:
      // impactService maps ANY 403 to PrivilegeError (services/impactService.js:33-38),
      // including an edge-originated one that never reached the Go handler, such as a
      // WAF block. Swallowing it to an empty list is deliberate — the alternative is a
      // failure banner on a cause the user cannot act on.
      //
      // IT IS SWALLOWED FROM THE UI, NOT FROM THE LOGS, AND THE "ONCE" BELOW IS
      // THE WHOLE DESIGN. Until this was added the residual was completely
      // silent: this branch returns BEFORE the logError below, and on a
      // background poll the !isBackgroundRefresh guard meant it did nothing
      // whatsoever -- so an unattributable 403 produced a request every
      // POLLING_INTERVALS.INCIDENT_IMPACTS for the life of the mount with zero
      // observability, which is not a trade anyone can evaluate. Logging it on
      // every poll would swap that silence for roughly two log lines a minute
      // per mounted card, and reducing exactly that kind of volume is why
      // US1950081 exists. So the ref records the problemNumber of the refusal
      // it already logged AND a successful read clears it (see the clear on the
      // success path above): one warning per TRANSITION INTO the refused state.
      // Repeated refusals with no successful read between them stay quiet; a
      // recovery re-arms the warning, so a later refusal on the SAME
      // problemNumber logs again; and a change of identity logs again too.
      // logWarning rather than logError because the outcome is HANDLED -- empty
      // list, no banner, nothing broken for the user -- and because the cause may
      // be entirely legitimate; what makes it worth recording is that the hook
      // cannot tell which.
      if (err.name === 'PrivilegeError') {
        if (forbiddenLoggedForRef.current !== problemNumber) {
          forbiddenLoggedForRef.current = problemNumber;
          logWarning(
            'useReportedImpacts.fetchImpacts',
            'Impacts request refused (403) for a problem the caller enabled; swallowed to an empty list. isBackgroundRefresh says what follows: false means this was an initial load and the interval is left unarmed, so it is the only request for this identity; true means an already-armed interval keeps polling. Logged once per TRANSITION INTO the refused state, NOT once per problem identity: a successful read clears the flag, so consecutive refusals with no successful read between them produce one warning, while a recovery followed by a new refusal on the same problemNumber produces a second. Two lines for one problemNumber therefore mean a successful read intervened, not that the identity changed and came back.',
            { problemNumber, isBackgroundRefresh }
          );
        }
        if (!isBackgroundRefresh) {
          setImpacts([]);
          setError(null);
        }
        return;
      }

      logError('useReportedImpacts.fetchImpacts', err, {
        problemNumber,
        isBackgroundRefresh,
        hasData: hasCurrentData
      });

      if (isBackgroundRefresh) {
        logWarning('useReportedImpacts.fetchImpacts', 'Background refresh failed, keeping existing data', {
          problemNumber,
          hasData: hasCurrentData
        });
      } else {
        setError('Failed to load reported impacts. Please try again.');
        setImpacts([]);
      }
    } finally {
      if (isBackgroundRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, [problemNumber, sortImpacts]);

  // Initial fetch when enabled
  useEffect(() => {
    const abortController = new AbortController();

    if (!enabled) {
      setImpacts([]);
      setError(null);
      setIsInitialLoad(true);
      return;
    }

    // AC10: RE-ARM THE INITIAL-LOAD GATE FOR THE NEW IDENTITY. The `!enabled`
    // branch above does not cover this, and the difference is a live defect
    // without these two lines. The interval is gated on `enabled && !isInitialLoad`
    // (see usePolling below), so an `isInitialLoad` left false by the PREVIOUS
    // problem's successful load keeps that problem's interval armed straight
    // through an identity change. `problemNumber` can change with `enabled`
    // UNCHANGED — that is the path this line, and only this line, covers, because
    // the `!enabled` branch above re-arms on a transition TO false and a constant
    // `enabled` never transitions.
    //
    // HISTORY, because the original example no longer reads the way it did: this
    // was written when ModernIncidentDetailPage passed
    // `isTemporary={!!data?.temporary}`, so a navigation in place from a loaded
    // problem to a PRBT left `enabled` true on the null-`data` render (`id` comes
    // from useParams and changes SYNCHRONOUSLY, while useIncidentDetails nulls
    // `data` on every incidentNumber change — useIncidentDetails.js:205), and the
    // already-armed interval 403d every POLLING_INTERVALS.INCIDENT_IMPACTS until
    // the detail fetch landed. That page now passes
    // `isTemporary={!data || !!data.temporary}` (ModernIncidentDetailPage.js:225),
    // so once `data` is null its identity change also drives `enabled` false and
    // takes the `!enabled` branch.
    //
    // THAT DID NOT RETIRE THIS LINE, AND IT IS NOT BELT-AND-BRACES — the detail
    // page still reaches it, for one render. useIncidentDetails calls setData(null)
    // inside a useEffect (useIncidentDetails.js:205), NOT during render, so the
    // render immediately after the route param changes still holds the PREVIOUS
    // record:
    //
    //   Render N    problemNumber = B (synchronous, useParams)
    //               data = A's record, still non-null
    //               -> isTemporary false -> enabled TRUE, isInitialLoad false
    //   effect      setData(null)
    //   Render N+1  data null -> isTemporary true -> enabled false -> `!enabled`
    //
    // Render N is exactly "identity changed, `enabled` unchanged", and THIS LINE is
    // what disarms the interval for it. ExpandedContent is NOT a second example of
    // that case: IncidentSection keys the card by `problem.number || problem.id`
    // (IncidentSection.js:221 and :252), so a different incident REMOUNTS and gets
    // `isInitialLoad = true` from useState instead. Mutation-tested: reverting this
    // line returns Expected 1, Received 5. Do not remove it.
    //
    // Re-arming the flag disarms the interval for the whole window, so the PRBT
    // costs exactly ONE refused request — the fetch below — and not a repeating
    // one, because the PrivilegeError branch in fetchImpacts returns WITHOUT
    // clearing `isInitialLoad`, so a first-response 403 never arms the interval at
    // all. A problem whose first response SUCCEEDS arms it as usual, one interval
    // period after that response rather than inheriting the previous problem's
    // clock.
    //
    // THE COST, STATED RATHER THAN GLOSSED: a navigation now behaves exactly like
    // a mount, on failure as well as on success. An initial fetch that fails for
    // any reason leaves the flag true and therefore never arms the interval —
    // already true on mount before this change and on `main` — so navigating from
    // a problem that polls to one whose first fetch 500s ends in the error banner
    // plus a manual refresh, not a self-healing 30-second retry. That is the
    // consistent reading of the existing gate, not a new policy.
    setIsInitialLoad(true);

    if (!problemNumber) return;

    fetchImpacts(false, abortController.signal);

    // Cleanup: abort BOTH in-flight requests when problemNumber changes, when
    // `enabled` flips, or on unmount -- this effect's own initial fetch AND any
    // in-flight background POLL.
    //
    // THE POLL ABORT IS LOAD-BEARING, AND IT WAS MISSING UNTIL 2026-09-14.
    // Nothing else cancels a poll on an identity change: the unmount-only effect
    // below cannot, because a problemNumber change does NOT unmount this hook.
    // ModernIncidentDetailPage renders IncidentReportedImpacts unconditionally
    // and passes the route param straight through, so navigating from one
    // incident to another re-renders the SAME component with a new
    // problemNumber. Without this abort, a poll issued for problem A resolves
    // after the switch to B and runs the success path, writing A's impacts into
    // state while B is displayed — a visible cross-problem data leak. That is
    // the same failure the unmount cleanup below prevents, reached by a
    // different route. (A superseded revision of this note gave a second reason,
    // that the late success also cleared a 403 latch B had just set and re-armed
    // a 30-second 403 loop. There is no such latch to clear. Nor was one removed
    // here: `main` has no latch either, so nothing in this PR's diff against it
    // is a removal -- read the asymmetry note above usePolling for what the hook
    // actually does with a 403. The cross-problem leak is reason enough on its own
    // and this abort stays.)
    //
    // SCOPE, SO THE ABOVE IS NOT READ AS MORE THAN IT IS: this abort closes the
    // LATE-RESPONSE route to a stale render. It does NOT close the SYNCHRONOUS
    // one, which is pre-existing and deliberately unchanged here. On an identity
    // change that keeps `enabled` TRUE this effect leaves `impacts` alone, so B's
    // heading renders over A's rows, and no abort can reach that: A's rows are already
    // COMMITTED STATE, written by a response that landed before the navigation,
    // so there is no request left whose cancellation would remove them.
    //
    // HOW LONG THAT LASTS, because an earlier revision of this paragraph said "until
    // B's own first response calls setImpacts" and that is no longer true. On the
    // detail-page route it is ONE RENDER: Render N+1 has `data` null, so `enabled`
    // goes false, the `!enabled` branch above runs setImpacts([]), and
    // `detailLoading={!data}` renders the skeleton rather than A's rows
    // (IncidentReportedImpacts.js:309). ExpandedContent never reaches it at all,
    // because a different incident remounts there — see the Render-N note above. (Do not
    // read that as "nothing is in flight for A" -- usually something is, which is
    // exactly what this cleanup aborts. That abort rejects with AbortError,
    // returns early at the top of the catch, and still runs fetchImpacts'
    // `finally`, which flips loading/refreshing off unconditionally and so can
    // briefly clear loading inside B's own load window.
    // useIncidentDetails.js:171-181 guards precisely that with
    // `if (!signal?.aborted)`; this hook does not. Pre-existing, unchanged here,
    // and deliberately not fixed under this story.) Clearing impacts on an
    // identity change would close the window, but that is a visible behaviour
    // change (a flash to empty on every navigation) and is outside this fix. Read
    // the paragraph above as "the late-response leak is closed", NOT as "stale
    // cross-problem rendering is impossible".
    return () => {
      abortController.abort();
      pollAbortRef.current?.abort();
    };
  }, [problemNumber, enabled, fetchImpacts]);

  // Background polling — each poll gets its own AbortController so
  // in-flight requests are cancelled if the component unmounts mid-poll.
  //
  // THE INTERVAL IS GATED ON `enabled && !isInitialLoad`, WHICH MAKES THE HOOK'S
  // RESPONSE TO A 403 ASYMMETRIC. Know the asymmetry before changing anything here,
  // and do not go looking for a latch in this PR's diff -- `main` has none either,
  // so nothing here was removed:
  //   * A 403 on an INITIAL load never arms the interval. The PrivilegeError branch
  //     in fetchImpacts returns WITHOUT clearing `isInitialLoad`, so the flag stays
  //     true and this gate stays false. One refused request, then silence.
  //   * A 403 on a BACKGROUND poll does not stop the interval. By then
  //     `isInitialLoad` is false and nothing sets it back, so the request recurs
  //     every POLLING_INTERVALS.INCIDENT_IMPACTS for the life of the mount.
  // US1950081 changed WHICH FETCHES COUNT AS INITIAL, not the rule: the
  // mount/identity effect above now re-arms `isInitialLoad` when problemNumber
  // changes, so a problem reached by NAVIGATION gets the first bullet instead of
  // inheriting the previous problem's already-armed interval. That effect is where
  // the reasoning lives; it is AC10 and it is not optional.
  //
  // WHAT US1950081 CHANGED IN THIS HOOK IS THE CALLER CONTRACT, NOT A BACKSTOP.
  // The refusal has exactly one designed cause — the backend declines impact
  // reporting on a temporary record (handler_impact.go:372, and the same test guards
  // all seven impact handlers; see handler_impact.go:5) — so the caller can know the
  // answer before asking, and now does: `enabled` is driven by the record's
  // `temporary` flag at the `useReportedImpacts(problemId, !isTemporary, ...)` call,
  // IncidentReportedImpacts.js:122, from both callers — `isTemporary={!!data.temporary}`
  // on the <IncidentReportedImpacts> element at ExpandedContent.js:171-177 (the prop is
  // the line at :176) off a non-null record, and ModernIncidentDetailPage.js:225
  // conservatively as `!data || !!data.temporary` so the null-`data` first render of a
  // PRBT link does not ask either. The designed refusal is PREVENTED at source rather
  // than reacted to after the fact.
  //
  // ExpandedContent PASSES THE PROP, IT DOES NOT GATE THE SECTION. A superseded
  // revision of this note said it "declines to render that component at all for a
  // temporary problem"; it does not, and deliberately so — gating the section on
  // `!data.temporary` hid the heading and broke e2e MUD-02, so the gate was reduced to
  // the prop. See the comment at ExpandedContent.js:164-177.
  //
  // THE GATE KEYS ON THE FLAG AND MUST NEVER BE REWRITTEN AS A `PRBT` PREFIX TEST.
  // `problem.Temporary` is the exact field handler_impact.go:372 refuses on, so the
  // flag cannot drift from the backend's rule. A number prefix would be a second,
  // independent definition of "temporary" with nothing keeping the two in sync.
  //
  // WHAT IS STILL OPEN, STATED SO NOBODY READS THE CALLER GATE AS TOTAL: the second
  // bullet above. A 403 the hook cannot attribute — a WAF block, a permission change
  // mid-session — arriving on a background poll leaves the interval running
  // UNBOUNDED, one request per POLLING_INTERVALS.INCIDENT_IMPACTS for the life of the
  // mount, and the app sits behind a WAF with rate-based rules. The blast radius is
  // bounded (pauseWhenHidden stops the interval on a hidden tab; it is one request
  // per 30s per mounted card) but the retry is not. It is left running deliberately:
  // the signal is ambiguous by construction — impactService maps ANY 403 to
  // PrivilegeError (services/impactService.js:33-38) — so stopping live updates
  // permanently on it would penalise a problem the user may well be permitted to see.
  // The branch does log one warning per TRANSITION INTO the refused state (read it for
  // why once and not per poll), so the loop is detectable in the log stream rather than
  // silent, which it was before that warning existed. DO NOT READ THE LOG LINE AS A
  // FIX. IF A BACKSTOP IS EVER WANTED, IT NEEDS A BOUNDED RETRY, NOT A LATCH.

  // ABORT THE IN-FLIGHT POLL ON UNMOUNT. Without this, a poll issued just before
  // the consumer unmounts resolves afterwards and runs the success path's state
  // setters on a dead component. ExpandedContent.js mounts and unmounts this hook's
  // consumer on every card expand/collapse, so an unmount with a request in flight is
  // the common case
  // here, not an edge one. Empty dep array on purpose: the cleanup must run at
  // unmount only, and it reads the ref's CURRENT value when it runs, so it does not
  // need the controller as a dependency. The OTHER route to a stale poll -- a
  // problemNumber change on a still-mounted hook -- is covered by the
  // mount/identity effect's cleanup above, not by this effect.
  useEffect(() => () => pollAbortRef.current?.abort(), []);

  usePolling(
    () => {
      pollAbortRef.current?.abort();
      pollAbortRef.current = new AbortController();
      return fetchImpacts(true, pollAbortRef.current.signal);
    },
    POLLING_INTERVALS.INCIDENT_IMPACTS,
    {
      enabled: enabled && !isInitialLoad,
      pauseWhenHidden: true
    }
  );

  /**
   * Mark a single impact as resolved.
   */
  const markResolved = useCallback(async (impact) => {
    try {
      await impactService.updateImpact(
        problemNumber,
        impact.id,
        buildUpdatePayload(impact, { status: 'resolved', statusDisplay: 'Resolved' })
      );
      pollAbortRef.current?.abort();
      pollAbortRef.current = new AbortController();
      await fetchImpacts(true, pollAbortRef.current.signal);
    } catch (err) {
      logError('useReportedImpacts.markResolved', err, {
        problemNumber,
        impactId: impact.id
      });
      throw err;
    }
  }, [problemNumber, fetchImpacts]);

  /**
   * Re-open a single resolved impact.
   */
  const markActive = useCallback(async (impact) => {
    try {
      await impactService.updateImpact(
        problemNumber,
        impact.id,
        buildUpdatePayload(impact, { status: 'active', statusDisplay: 'Active' })
      );
      pollAbortRef.current?.abort();
      pollAbortRef.current = new AbortController();
      await fetchImpacts(true, pollAbortRef.current.signal);
    } catch (err) {
      logError('useReportedImpacts.markActive', err, {
        problemNumber,
        impactId: impact.id
      });
      throw err;
    }
  }, [problemNumber, fetchImpacts]);

  return {
    impacts,
    loading,
    refreshing,
    lastUpdated,
    error,
    refresh: () => {
      pollAbortRef.current?.abort();
      pollAbortRef.current = new AbortController();
      return fetchImpacts(true, pollAbortRef.current.signal);
    },
    markResolved,
    markActive
  };
};
